import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { adminApiService } from '../services/adminApi';
import { addNotification } from '../store/slices/uiSlice';

const CONFIRM_WORD = 'confirm';

const CATEGORY_KEYS = [
  'members',
  'minor_members',
  'organization',
  'serve_positions',
  'attendance',
  'events',
  'tithe',
  'forms',
  'pos',
  'rooms',
  'posts',
];

const SystemSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [step, setStep] = useState(0); // 0: idle, 1: warning dialog, 2: type-confirm dialog
  const [confirmText, setConfirmText] = useState('');
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState([]);

  const allChecked = selected.length === CATEGORY_KEYS.length;
  const selectedLabels = selected.map((key) => t(`settings.dangerZone.categories.${key}`));

  const toggleCategory = (key) => {
    setSelected((prev) => (
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    ));
  };

  const toggleAll = () => {
    setSelected(allChecked ? [] : [...CATEGORY_KEYS]);
  };

  const openWarning = () => {
    if (selected.length === 0) return;
    setConfirmText('');
    setResult(null);
    setStep(1);
  };

  const closeDialog = () => {
    if (clearing) return;
    setStep(0);
    setConfirmText('');
  };

  const handleClear = async () => {
    if (confirmText !== CONFIRM_WORD) return;
    setClearing(true);
    try {
      const data = await adminApiService.system.clearAllData(selected);
      setResult(data);
      dispatch(
        addNotification({
          type: 'success',
          title: t('settings.clear.successTitle'),
          message: t('settings.clear.successMsg', { count: data.total_rows_deleted }),
        })
      );
      setStep(0);
      setConfirmText('');
    } catch (error) {
      dispatch(
        addNotification({
          type: 'error',
          title: t('settings.clear.errorTitle'),
          message: error.message || t('settings.clear.errorMsg'),
        })
      );
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page intro */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          {t('settings.title')}
        </h3>
        <p className="text-gray-600 text-sm mt-1">{t('settings.description')}</p>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-lg shadow-sm border-2 border-red-300 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-semibold text-red-700">
                {t('settings.dangerZone.title')}
              </h3>
            </div>
            <p className="text-gray-700 text-sm mt-2">
              {t('settings.dangerZone.description')}
            </p>
            <label className="mt-4 flex items-center gap-2 text-sm font-medium text-gray-800">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={toggleAll}
                disabled={clearing}
              />
              {t('settings.dangerZone.selectAll')}
            </label>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATEGORY_KEYS.map((key) => (
                <label key={key} className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selected.includes(key)}
                    onChange={() => toggleCategory(key)}
                    disabled={clearing}
                  />
                  <span>{t(`settings.dangerZone.categories.${key}`)}</span>
                </label>
              ))}
            </div>
            {selected.length === 0 && (
              <p className="text-xs text-red-600 mt-3">{t('settings.dangerZone.noneSelected')}</p>
            )}
          </div>
          <button
            onClick={openWarning}
            className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
            disabled={clearing || selected.length === 0}
          >
            {t('settings.dangerZone.button')}
          </button>
        </div>
      </div>

      {/* Result summary */}
      {result && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            {t('settings.clear.resultTitle')}
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            {t('settings.clear.resultMeta', {
              username: result.cleared_by?.username,
              count: result.total_rows_deleted,
            })}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">{t('settings.clear.resultTable')}</th>
                  <th className="py-2 font-medium">{t('settings.clear.resultRows')}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.tables_cleared || {}).map(([table, rows]) => (
                  <tr key={table} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-mono text-gray-800">{table}</td>
                    <td className="py-2 text-gray-600">{rows}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(result.preserved_tables || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-1">
                {t('settings.clear.resultPreserved')}
              </p>
              <div className="flex flex-wrap gap-2">
                {result.preserved_tables.map((table) => (
                  <span
                    key={table}
                    className="px-2 py-1 bg-green-50 border border-green-200 rounded font-mono text-xs text-green-800"
                  >
                    {table}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dialog 1 — warning */}
      {step === 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('settings.clear.confirmTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('settings.clear.confirmBody', { categories: selectedLabels.join('、') })}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDialog}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => setStep(2)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('settings.clear.continue')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog 2 — type CONFIRM_WORD */}
      {step === 2 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('settings.clear.finalTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {t('settings.clear.finalBody')}
            </p>
            <p className="text-sm text-gray-800 mb-3">
              {t('settings.clear.typePrompt')}{' '}
              <code className="px-1.5 py-0.5 bg-red-50 border border-red-200 rounded font-mono font-semibold text-red-700">
                {CONFIRM_WORD}
              </code>
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleClear();
              }}
              autoFocus
              disabled={clearing}
              placeholder={CONFIRM_WORD}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4 font-mono"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDialog}
                disabled={clearing}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleClear}
                disabled={confirmText !== CONFIRM_WORD || clearing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {clearing ? t('settings.clear.clearing') : t('settings.clear.finalButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
